import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Eye, MessageCircle, Star, Tag } from 'lucide-react';
import type { BlogPost } from '@/types/blog';

interface BlogCardProps {
  post: BlogPost;
}

export function BlogCard({ post }: BlogCardProps) {
  const { t } = useTranslation();

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block overflow-hidden rounded-xl border bg-card shadow-sm transition-all hover:shadow-lg hover:-translate-y-1"
    >
      {post.coverImage ? (
        <div className="aspect-video overflow-hidden">
          <img
            src={post.coverImage}
            alt={post.title}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        </div>
      ) : (
        <div className="aspect-video bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
          <span className="text-4xl font-bold text-white/30">
            {post.title.charAt(0)}
          </span>
        </div>
      )}

      <div className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
            {post.category?.name || t('blogPage.uncategorized')}
          </span>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {new Date(post.createdAt).toLocaleDateString()}
          </span>
        </div>

        <h3 className="mb-2 text-lg font-semibold leading-tight group-hover:text-emerald-600 transition-colors line-clamp-2">
          {post.title}
        </h3>

        <p className="mb-4 text-sm text-muted-foreground line-clamp-2">
          {post.excerpt}
        </p>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" />
              {post.viewCount}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="h-3.5 w-3.5" />
              {post.commentCount}
            </span>
            {Number(post.averageRating ?? 0) > 0 && (
              <span className="flex items-center gap-1">
                <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                {Number(post.averageRating).toFixed(1)}
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {post.authorName}
          </span>
        </div>

        {post.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-0.5 rounded-md bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
              >
                <Tag className="h-2.5 w-2.5" />
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
